import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Paper,
  TextField,
  useMediaQuery,
} from "@mui/material";
import GenieText from "../../../../components/core/genie-text/GenieText";
import { theme } from "../../../../components/special/genie-theme/genie-theme";
import { isValidEmail } from "../../../../utils/email";
import useInviteBuyer from "../../../../queries/routes/useInviteBuyer";

interface InviteDialogProps {
  open: boolean;
  setClosed: () => void;
}

export default function InviteDialog({ open, setClosed }: InviteDialogProps) {
  const { org_id } = useParams();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { mutate: inviteBuyer } = useInviteBuyer();

  const [currentEmail, setCurrentEmail] = React.useState<string>("");
  const [emails, setEmails] = React.useState<string[]>([]);

  const handleClose = () => {
    setClosed();
    setEmails([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && isValidEmail(currentEmail)) {
      setEmails([...emails, currentEmail]);
      setCurrentEmail("");
    }
  };

  const submit = () => {
    inviteBuyer(
      {
        to_organization_id: org_id!,
        to_emails: emails,
      },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  return (
    <Dialog fullScreen={fullScreen} open={open} onClose={handleClose}>
      <DialogTitle>Invite your customers</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>
          Invite new users to your store by email.
        </DialogContentText>
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          mb={2}
        >
          <TextField
            sx={{ width: "60%" }}
            label="Email"
            required
            placeholder={"Add one at a time"}
            type={"email"}
            id="email"
            variant="outlined"
            onKeyDown={handleKeyDown}
            onChange={(event) => setCurrentEmail(event.target.value)}
            value={currentEmail}
          />
          <Button
            sx={{ ml: 2 }}
            variant={"outlined"}
            disabled={!isValidEmail(currentEmail)}
            onClick={() => {
              setEmails([...emails, currentEmail]);
              setCurrentEmail("");
            }}
          >
            Add
          </Button>
        </Box>

        <Paper
          variant={"outlined"}
          sx={{ mb: 2, p: 1, width: !fullScreen ? 552 : "100%" }}
          elevation={0}
        >
          {emails.length === 0 && (
            <GenieText fontSize={"tiny"}>
              Emails added will appear here
            </GenieText>
          )}
          {emails.map((email) => {
            return <Chip sx={{ mr: 2, mb: 1 }} key={email} label={email} />;
          })}
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button disabled={emails.length === 0} onClick={submit}>
          Send Invites
        </Button>
      </DialogActions>
    </Dialog>
  );
}
